/**
 * components/BetaApplication/hooks/useBetaApplicationData.ts
 * Beta Application Data Hook
 *
 * Provides access to beta application data from WizardDataProvider context.
 * Follows the same pattern as useOnboardingData for consistency.
 */

// 1. External dependencies
import { useContext } from 'react';

// 2. Internal absolute imports
import { WizardDataContext, type WizardDataContextValue } from '@/components/shared/layouts/WizardLayout/WizardDataProvider';

// 3. Relative imports
import type { BetaApplicationDraft } from '../types/BetaApplicationDraft';

/**
 * Hook to access beta application data from WizardDataProvider
 *
 * This hook provides type-safe access to the beta application draft data
 * and all the data management functions from WizardDataProvider.
 *
 * @returns Beta application data context with proper typing
 * @throws Error if used outside WizardDataProvider
 */
export function useBetaApplicationData(): WizardDataContextValue<BetaApplicationDraft> {
  const context = useContext(WizardDataContext);

  if (!context) {
    throw new Error('useBetaApplicationData must be used within a WizardDataProvider');
  }

  // Type assertion is safe here because the WizardDataProvider is typed
  // when it's set up in App.tsx with BetaApplicationDraft
  return context as unknown as WizardDataContextValue<BetaApplicationDraft>;
}

/**
 * Convenience hook that returns just the draft data
 *
 * @returns The current beta application draft or null if loading
 */
export function useBetaApplicationDraft(): BetaApplicationDraft | null {
  const { data } = useBetaApplicationData();
  return data;
}